import { useLazyQuery } from "@apollo/client";
import Button from "components/base/Button";
import { Input } from "components/base/Form";
import { InlineSpinner } from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { useContext } from "react";
import { GTMProjectContext } from "../GTMProjectContext";
import { FETCH_GTM_SOURCE_PRODUCT_IMPORT_DATA } from "../graphql";
import useSaveGTMProject from "../useSaveGTMProject";

function buildCustomsSize(product) {
  if (!product) return "";
  if (product.shape === "ball") return `${product.ballDiameter || 0} mm`;
  if (product.shape === "cube")
    return `${product.cubeX || 0} x ${product.cubeZ || 0} x ${product.cubeY || 0} mm`;
  if (product.shape === "flat")
    return `${product.flatX || 0} x ${product.flatY || 0} mm`;
  if (product.shape === "cylindrical")
    return `${product.cylindricalDiameter || 0} x ${product.cylindricalHeight || 0} mm`;
  if (product.shape === "multi") return product.multiSizes || "";
  return "";
}

function buildImportedLogisticsData(sourceProduct, currentState) {
  const productLine = sourceProduct?.productLine || {};
  const materialNames = (productLine.materials || [])
    .map((i) => i.material?.name)
    .filter(Boolean)
    .join(", ");
  const abPrice = (sourceProduct.activePrices || []).find(
    (i) => i.company === "AB",
  );

  return {
    ...currentState,
    hsCode: productLine.hsCode || sourceProduct.hsCode || "",
    hsCodeForEu: productLine.hsCodeForEu || "",
    hsCodeForUs: productLine.hsCodeForUs || "",
    customsName: productLine.customsName || sourceProduct.name || "",
    customsBrand: productLine.customsBrand || "",
    customsUsage: productLine.customsUsage || "",
    customsValue: productLine.customsValue || abPrice?.price || "",
    customsSize: productLine.customsSize || buildCustomsSize(sourceProduct),
    customsMaterials: productLine.customsMaterials || materialNames,
  };
}

const LogisticsView = () => {
  const { originalGTMProject, saveProject, saveProjectRes, autoSave } =
    useContext(GTMProjectContext);
  const [fetchSourceProduct, fetchSourceProductRes] = useLazyQuery(
    FETCH_GTM_SOURCE_PRODUCT_IMPORT_DATA,
    {
      fetchPolicy: "network-only",
    },
  );

  const { state, onChange, setValues, loading } = useSaveGTMProject(
    originalGTMProject.id,
    "logisticsData",
    JSON.parse(originalGTMProject.logisticsData),
    1000,
  );

  function importSourceProductInfoHandler() {
    if (!originalGTMProject.sourceProduct?.id) {
      Alert("error", "Please choose a source product first.");
      return;
    }
    const sourceProductLabel = `[${originalGTMProject.sourceProduct.number}] ${originalGTMProject.sourceProduct.name}`;
    if (
      !window.confirm(
        `Are you sure you want to import source product logistics info?\n\nSource Product: ${sourceProductLabel}`,
      )
    ) {
      return;
    }

    fetchSourceProduct({
      variables: { id: originalGTMProject.sourceProduct.id },
    }).then((res) => {
      const sourceProduct = res.data?.product;
      if (!sourceProduct) {
        Alert("error", "Source product not found.");
        return;
      }
      const importedData = buildImportedLogisticsData(sourceProduct, state);
      setValues(importedData);
      autoSave({
        id: originalGTMProject.id,
        input: {
          logisticsData: JSON.stringify(importedData, " ", 2),
        },
      });
      Alert("success", "Source product logistics imported.");
    });
  }

  function confirmHandler() {
    if (
      window.confirm(
        "Are you sure you want to confirm the Logistics Information for this project?",
      )
    ) {
      saveProject({
        variables: {
          id: originalGTMProject.id,
          logisticsConfirmedAt: new Date().toISOString(),
        },
      });
    }
  }

  const disabled = !!originalGTMProject.logisticsConfirmedAt;

  return (
    <div className="card">
      <div className="flex justify-between">
        <h5>
          Logistics (<b className="text-orange-500">Sharon</b>)
        </h5>
        <div className="flex items-center space-x-3">
          <Button
            onClick={importSourceProductInfoHandler}
            loading={fetchSourceProductRes.loading}
            disabled={!originalGTMProject.sourceProduct?.id || disabled}
          >
            Import Source Product Info
          </Button>
          {loading && <InlineSpinner size={18} />}
        </div>
      </div>

      {originalGTMProject.logisticsConfirmedAt && (
        <div className="text-green-600 font-bold mt-1">
          🟢 Confirmed at{" "}
          {new Date(originalGTMProject.logisticsConfirmedAt).toLocaleString()}.
        </div>
      )}

      <div className="-mx-2 mt-2 whitespace-nowrap">
        <table>
          <tbody>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>HS Code:</td>
              <td className="w-full">
                <Input
                  className="w-full"
                  value={state.hsCode}
                  onChange={(e) => onChange("hsCode", e.target.value)}
                  readOnly={disabled}
                />
              </td>
            </tr>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>HS Code for EU:</td>
              <td className="w-full">
                <Input
                  className="w-full"
                  value={state.hsCodeForEu}
                  onChange={(e) => onChange("hsCodeForEu", e.target.value)}
                  readOnly={disabled}
                />
              </td>
            </tr>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>HS Code for US:</td>
              <td className="w-full">
                <Input
                  className="w-full"
                  value={state.hsCodeForUs}
                  onChange={(e) => onChange("hsCodeForUs", e.target.value)}
                  readOnly={disabled}
                />
              </td>
            </tr>

            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td className="pt-6">
                <h6>Product Customs Form</h6>
              </td>
            </tr>

            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>Name: </td>
              <td>
                <Input
                  className="w-full"
                  value={state.customsName}
                  onChange={(e) => onChange("customsName", e.target.value)}
                  placeholder="For example: 球拍和毽子"
                  readOnly={disabled}
                />
              </td>
            </tr>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>Brand: </td>
              <td>
                <Input
                  className="w-full"
                  value={state.customsBrand}
                  onChange={(e) => onChange("customsBrand", e.target.value)}
                  readOnly={disabled}
                />
              </td>
            </tr>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>Usage: </td>
              <td>
                <Input
                  className="w-full"
                  value={state.customsUsage}
                  onChange={(e) => onChange("customsUsage", e.target.value)}
                  readOnly={disabled}
                />
              </td>
            </tr>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>Value: </td>
              <td>
                <Input
                  className="w-full"
                  value={state.customsValue}
                  onChange={(e) => onChange("customsValue", e.target.value)}
                  readOnly={disabled}
                />
              </td>
            </tr>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>Size: </td>
              <td>
                <Input
                  className="w-full"
                  value={state.customsSize}
                  onChange={(e) => onChange("customsSize", e.target.value)}
                  readOnly={disabled}
                />
              </td>
            </tr>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>Materials: </td>
              <td>
                <Input
                  className="w-full"
                  value={state.customsMaterials}
                  onChange={(e) => onChange("customsMaterials", e.target.value)}
                  readOnly={disabled}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {!originalGTMProject.logisticsConfirmedAt && (
        <div className="mt-4 flex justify-end">
          <Button onClick={confirmHandler} loading={saveProjectRes.loading}>
            Confirm Logistics Information
          </Button>
        </div>
      )}
    </div>
  );
};

export default LogisticsView;
